/**
 * Canonical, read-only group topology for the canvas.
 *
 * Group metadata can be stale while another device is writing. Rendering must
 * never let a duplicated, cyclic, or missing reference hide a node. The first
 * valid ownership relation in canvas order wins; invalid relations are ignored.
 */

function rawChildIds(node, nodeById) {
  if (!Array.isArray(node?.groupChildIds)) return [];
  const ids = [];
  const seen = new Set();
  for (const value of node.groupChildIds) {
    if (typeof value !== 'string' || !value || value === node.id || seen.has(value) || !nodeById.has(value)) continue;
    seen.add(value);
    ids.push(value);
  }
  return ids;
}

function buildCanvasGroupTopology(nodes) {
  const sourceNodes = Array.isArray(nodes) ? nodes.filter((node) => typeof node?.id === 'string' && node.id) : [];
  const nodeById = new Map(sourceNodes.map((node) => [node.id, node]));
  const groupChildIdsByGroupId = new Map();
  const groupIdByChildId = new Map();

  const wouldCreateCycle = (groupId, childId) => {
    let cursor = groupIdByChildId.get(groupId);
    const seen = new Set([groupId]);
    while (cursor) {
      if (cursor === childId || seen.has(cursor)) return true;
      seen.add(cursor);
      cursor = groupIdByChildId.get(cursor);
    }
    return false;
  };

  for (const node of sourceNodes) {
    const childIds = [];
    for (const childId of rawChildIds(node, nodeById)) {
      if (groupIdByChildId.has(childId) || wouldCreateCycle(node.id, childId)) continue;
      groupIdByChildId.set(childId, node.id);
      childIds.push(childId);
    }
    if (childIds.length > 0) groupChildIdsByGroupId.set(node.id, childIds);
  }

  const groupIds = new Set(groupChildIdsByGroupId.keys());
  const descendantIdsByGroupId = new Map();
  const leafDescendantIdsByGroupId = new Map();
  const collectDescendants = (groupId, leafOnly) => {
    const output = [];
    const visit = (nodeId, ancestors) => {
      if (ancestors.has(nodeId)) return;
      output.push(nodeId);
      if (!groupIds.has(nodeId)) return;
      const nextAncestors = new Set(ancestors);
      nextAncestors.add(nodeId);
      for (const childId of groupChildIdsByGroupId.get(nodeId) || []) visit(childId, nextAncestors);
    };
    for (const childId of groupChildIdsByGroupId.get(groupId) || []) visit(childId, new Set([groupId]));
    return leafOnly ? output.filter((id) => !groupIds.has(id)) : output;
  };
  for (const groupId of groupIds) {
    descendantIdsByGroupId.set(groupId, collectDescendants(groupId, false));
    leafDescendantIdsByGroupId.set(groupId, collectDescendants(groupId, true));
  }

  return {
    nodeById,
    groupIds,
    groupChildIdsByGroupId,
    groupIdByChildId,
    descendantIdsByGroupId,
    leafDescendantIdsByGroupId,
  };
}

function buildCanvasGroupPresentation(nodes) {
  const topology = buildCanvasGroupTopology(nodes);
  const collapsedGroupIds = new Set();
  for (const groupId of topology.groupIds) {
    if (topology.nodeById.get(groupId)?.groupCollapsed === true) collapsedGroupIds.add(groupId);
  }

  const visibleCollapsedGroupIdByNodeId = new Map();
  for (const nodeId of topology.nodeById.keys()) {
    let cursor = nodeId;
    let visibleGroupId = null;
    const seen = new Set();
    while (topology.groupIdByChildId.has(cursor) && !seen.has(cursor)) {
      seen.add(cursor);
      const parentGroupId = topology.groupIdByChildId.get(cursor);
      if (collapsedGroupIds.has(parentGroupId)) visibleGroupId = parentGroupId;
      cursor = parentGroupId;
    }
    if (visibleGroupId) visibleCollapsedGroupIdByNodeId.set(nodeId, visibleGroupId);
  }

  const hiddenNodeIds = new Set();
  for (const nodeId of topology.nodeById.keys()) {
    if (!topology.groupIds.has(nodeId) && visibleCollapsedGroupIdByNodeId.has(nodeId)) hiddenNodeIds.add(nodeId);
  }

  const visibleCollapsedGroupIds = new Set();
  for (const groupId of collapsedGroupIds) {
    if (!visibleCollapsedGroupIdByNodeId.has(groupId)) visibleCollapsedGroupIds.add(groupId);
  }

  return {
    ...topology,
    collapsedGroupIds,
    hiddenNodeIds,
    visibleCollapsedGroupIds,
    visibleCollapsedGroupIdByNodeId,
  };
}

function expandCanvasGroupDeletionNodeIds(nodes, nodeIds) {
  const topology = buildCanvasGroupTopology(nodes);
  const expanded = new Set();
  for (const nodeId of Array.isArray(nodeIds) ? nodeIds : []) {
    if (!topology.nodeById.has(nodeId)) continue;
    expanded.add(nodeId);
    for (const descendantId of topology.descendantIdsByGroupId.get(nodeId) || []) expanded.add(descendantId);
  }
  return expanded;
}

function resolveCanvasGroupLeafNodeIds(nodes, nodeIds) {
  const topology = buildCanvasGroupTopology(nodes);
  const resolved = new Set();
  for (const nodeId of Array.isArray(nodeIds) ? nodeIds : []) {
    if (!topology.nodeById.has(nodeId)) continue;
    if (topology.groupIds.has(nodeId)) {
      for (const leafId of topology.leafDescendantIdsByGroupId.get(nodeId) || []) resolved.add(leafId);
    } else {
      resolved.add(nodeId);
    }
  }
  return Array.from(resolved);
}

function projectCanvasEdgesForGroupPresentation(edges, presentation, options = {}) {
  const sourceHandle = options.groupSourceHandle || 'group-source';
  const targetHandle = options.groupTargetHandle || 'group-target';
  const projected = [];
  const seen = new Set();
  for (const edge of Array.isArray(edges) ? edges : []) {
    if (!edge?.source || !edge?.target) continue;
    const nextSource = presentation.visibleCollapsedGroupIdByNodeId.get(edge.source) || edge.source;
    const nextTarget = presentation.visibleCollapsedGroupIdByNodeId.get(edge.target) || edge.target;
    // A relation wholly inside a collapsed group is represented by the group,
    // not a meaningless self-loop.
    if (nextSource === nextTarget) continue;
    const sourceChanged = nextSource !== edge.source;
    const targetChanged = nextTarget !== edge.target;
    const nextEdge = sourceChanged || targetChanged
      ? {
          ...edge,
          id: `group-proxy:${edge.id}:${nextSource}:${nextTarget}`,
          source: nextSource,
          target: nextTarget,
          sourceHandle: sourceChanged ? sourceHandle : edge.sourceHandle,
          targetHandle: targetChanged ? targetHandle : edge.targetHandle,
        }
      : edge;
    const dedupeKey = [
      nextEdge.source,
      nextEdge.target,
      nextEdge.type || '',
      nextEdge.data?.relationType || '',
      nextEdge.data?.relationLabel || '',
      nextEdge.sourceHandle || '',
      nextEdge.targetHandle || '',
    ].join('::');
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    projected.push(nextEdge);
  }
  return projected;
}

module.exports = {
  buildCanvasGroupTopology,
  buildCanvasGroupPresentation,
  expandCanvasGroupDeletionNodeIds,
  resolveCanvasGroupLeafNodeIds,
  projectCanvasEdgesForGroupPresentation,
};
